import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Text,
  Icon,
  RadioGroup,
  RadioGroupItem,
  useToast,
} from '@flatfile/baseline'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Flatfile } from '@flatfile/api-internal'
import { api, AppHTTPService } from '../../api/http-service'
import { useEventSubscriber } from '../../hooks/useEventSubscriber'
import { logger } from '../../utils/logging'

interface DownloadAgentModalProps {
  agent: Flatfile.Agent
  opened: boolean
  onClose: () => void
}

export const DownloadAgentModal = ({ agent, opened, onClose }: DownloadAgentModalProps) => {
  const [downloadType, setDownloadType] = useState<'source' | 'system_combined' | null>(null)
  const [downloading, setDownloading] = useState(false)
  const { toast } = useToast()

  // Add a ref to store the current job ID for the event subscriber
  const currentJobRef = useRef<string | null>(null)

  // Subscribe to job events
  useEventSubscriber(['job:completed', 'job:failed'], async (event) => {
    // Only process events for our current job and ensure it's an agent export job
    if (event.context.jobId === currentJobRef.current && event.payload?.job === 'agent:agent-export') {
      if (event.topic === 'job:completed') {
        try {
          // Fetch the job details to get the download URL
          const jobResponse = await api.jobs.get(event.context.jobId)
          const job = jobResponse.data

          if (job.outcome?.next?.type === 'download' && job.outcome.next.url) {
            await handleDownloadFile(job.outcome.next.url)
            setDownloading(false)
            onClose()
            toast({
              title: 'Success',
              description: `Successfully downloaded ${agent.slug} agent`,
            })
          } else {
            throw new Error('Job completed but no download URL was provided')
          }
        } catch (error) {
          setDownloading(false)
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Failed to download agent',
          })
          onClose()
        }
      } else if (event.topic === 'job:failed') {
        try {
          const jobResponse = await api.jobs.get(event.context.jobId)
          const job = jobResponse.data

          setDownloading(false)

          // Check if the error is related to missing source maps
          if (job.info?.includes('Source map not found')) {
            toast({
              variant: 'destructive',
              title: 'Unable to download agent',
              description:
                'This agent may have been created before source maps were supported. Only agents created with the latest Flatfile CLI can be downloaded.',
            })
            onClose()
          } else {
            toast({
              variant: 'destructive',
              title: 'Error',
              description: 'Failed to download agent',
            })
            onClose()
          }
        } catch (error) {
          setDownloading(false)
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Failed to download agent',
          })
          onClose()
        }
      }
    }
  })

  const handleDownloadFile = async (downloadUrl: string) => {
    try {
      const downloadResponse = await AppHTTPService.axios.get(downloadUrl, { responseType: 'blob' })

      const blob = new Blob([downloadResponse.data], { type: 'application/gzip' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${agent.slug}-${downloadType}.tgz`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to download agent file',
      })
      logger.error('Error downloading agent file:', error)
    }
  }

  const handleDownload = useCallback(async () => {
    if (!downloadType) return

    setDownloading(true)
    try {
      // Create an agent export job (bypass SDK union serializer to preserve config)
      const jobResponse = await AppHTTPService.axios.post('/v1/jobs', {
        type: 'agent',
        operation: 'agent-export',
        source: agent.id,
        environmentId: agent.environmentId,
        config: {
          exportType: downloadType.toUpperCase(),
        },
        trigger: 'immediate',
        status: 'executing',
      })

      // Store the job ID in our ref for the event subscriber
      currentJobRef.current = jobResponse.data?.data?.id
    } catch (error) {
      setDownloading(false)
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to start agent export',
      })
      logger.error('Error starting agent export:', error)
    }
  }, [downloadType, agent, toast])

  // Auto-start download for non-system agents
  useEffect(() => {
    if (opened && !agent.isSystem) {
      setDownloadType('source')
      handleDownload()
    }
  }, [opened, agent, handleDownload])

  return (
    <Dialog open={opened} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Download {agent.slug} agent</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col space-y-4'>
          {agent.isSystem ? (
            <>
              <Text className='text-sm'>Choose how you would like to download your agent:</Text>

              <RadioGroup
                value={downloadType || ''}
                onValueChange={(value) => setDownloadType(value as 'source' | 'system_combined')}
                className='flex flex-col space-y-4'
              >
                <div className='flex items-start space-x-2'>
                  <RadioGroupItem value='source' id='source' className='mt-2' />

                  <label htmlFor='source' className='cursor-pointer'>
                    <Text className='text-sm font-medium'>Source download</Text>
                    <div className='flex items-start'>
                      <Text className='text-xs text-muted'>Download the original source code of your agent</Text>
                    </div>
                  </label>
                </div>

                {(agent.template === 'constraints' || agent.template === 'autobuild') && (
                  <div className='flex items-start space-x-2'>
                    <RadioGroupItem value='system_combined' id='system_combined' className='mt-2' />

                    <label htmlFor='system_combined' className='cursor-pointer'>
                      <Text className='text-sm font-medium'>System Combined download</Text>
                      <div className='flex items-start'>
                        <Text className='text-xs text-muted'>
                          Download a bundled version of all of your autobuild and build mode agents combined with all
                          dependencies inlined
                        </Text>
                      </div>
                    </label>
                  </div>
                )}
              </RadioGroup>

              <Button
                className='w-full mt-4'
                onClick={handleDownload}
                disabled={!downloadType || downloading}
                icon={downloading ? 'loader' : undefined}
                iconProps={downloading ? { className: 'animate-spin', size: 16 } : undefined}
              >
                {downloading ? 'Downloading...' : 'Download agent'}
              </Button>
            </>
          ) : (
            <div className='border rounded-md p-6 mt-4 min-h-[100px] flex items-center justify-center'>
              <div className='flex items-center gap-2'>
                {downloading && <Icon name='loader' className='animate-spin' size={16} />}
                <Text className='text-sm text-center'>
                  {downloading ? 'Downloading agent...' : 'Preparing download...'}
                </Text>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
